import { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ContainerCard, EmployeeDetails, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { Check, Eye, LucideClipboardCheck, RemoveFormatting, X, Undo2, FileText, ThumbsUp, ThumbsDown, History, RotateCcw, Rewind, Download, ChevronLeft, ChevronRight, UserX, ListChecks, CheckCheck, BadgeCheck } from "lucide-react";
import { ADVANCE_REQUEST_URL, LEAVE_REQUEST_URL, REIMBURESMENT_REQUEST_URL, RESIGNATION_REQUEST_URL } from "../../constants/api";
import { truncateText } from "../../components/common/CommonFunctions";
import { Button } from "../../components/ui/button";
import { ConfirmPopupModal, DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import { cn } from "../../utils/tailwindUtils";
import { DatePicker, Image } from "antd";
import dayjs from 'dayjs';
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";

const ApprovalInbox = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { update } = useApiMethods()
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [reimbursements, setReimbursements] = useState([]);
    const [advanceSalary, setAdvanceSalary] = useState([]);
    const [resignationrequest, setResignationrequest] = useState([]);

    const initialTab = ['leave', 'reimbursement', 'advance', 'resign'].includes(location.state?.activeTab)
        ? location.state.activeTab
        : 'leave';
    const [activeTab, setActiveTab] = useState(initialTab);
    const [totalRecords, setTotalRecords] = useState(0)
    const [serverTrigger, setServerTrigger] = useState(false);

    // Dialog states
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showRevertDialog, setShowRevertDialog] = useState(false);
    const [showApprovalLog, setShowApprovalLog] = useState(false);
    const [showApproveDialog, setShowApproveDialog] = useState(false);
    const [showRejectDialog, setShowRejectDialog] = useState(false);
    const [revertReason, setRevertReason] = useState('');
    const [approvalNotes, setApprovalNotes] = useState('');
    const [debitFrom, setDebitFrom] = useState('');
    const [selectedRequestForReject, setSelectedRequestForReject] = useState(null);
    // Reimbursement approver can pay a smaller amount than requested. Default to the requested
    // amount; if the approver lowers it, the backend tags the request as 'partial'.
    const [reimbursementApprovedAmount, setReimbursementApprovedAmount] = useState('');

    // Resignation approval form (only used when the resignation dialog is open)
    const [resignationApproval, setResignationApproval] = useState({
        notice_period: '',
        last_working_day: null,
        approval_comments: '',
    });

    // Prefill resignation approval fields from the selected row when the dialog opens
    useEffect(() => {
        if (!showApproveDialog || !selectedRequest || activeTab !== 'resign') return;
        const isFinalLevel = String(selectedRequest.level) === '2';
        const np = isFinalLevel ? selectedRequest.final_notice_period : selectedRequest.notice_period;
        const lwdRaw = isFinalLevel ? selectedRequest.final_last_working_day : selectedRequest.last_working_day;
        const comments = isFinalLevel ? selectedRequest.final_approval_comments : selectedRequest.approval_comments;
        setResignationApproval({
            notice_period: (np !== null && np !== undefined && np !== '') ? String(np) : '',
            last_working_day: lwdRaw && lwdRaw !== 'NAT' && lwdRaw !== 'NaT' ? dayjs(lwdRaw) : null,
            approval_comments: comments || '',
        });
    }, [showApproveDialog, selectedRequest, activeTab]);

    // Default the approved-amount input to the requested amount whenever the reimbursement
    // approval dialog opens; approvers can edit it to trigger a 'partial' approval.
    useEffect(() => {
        if (!showApproveDialog || !selectedRequest || activeTab !== 'reimbursement') return;
        const requested = selectedRequest.requested_amount ?? selectedRequest.amount ?? '';
        setReimbursementApprovedAmount(requested === null || requested === undefined ? '' : String(requested));
    }, [showApproveDialog, selectedRequest, activeTab]);

    // Loading states
    const [approveLoading, setApproveLoading] = useState(false);
    const [rejectLoading, setRejectLoading] = useState(false);
    const [revertLoading, setRevertLoading] = useState(false);
    const [showProcessedDialog, setShowProcessedDialog] = useState(false);
    const [processedRemarks, setProcessedRemarks] = useState('');
    const [processedLoading, setProcessedLoading] = useState(false);

    // Common handlers

    const handleRejectRequest = () => {
        // console.log('Rejecting request:', selectedRequest, 'Notes:', approvalNotes);
        setShowApproveDialog(false);
        setApprovalNotes('');
    };

    const handleApproveRequest = async () => {
        setApproveLoading(true);
        let payload;

        console.log(selectedRequest);

        // Validation for advance salary
        if (selectedRequest.approval_type === 'Advance Salary' && (!debitFrom || debitFrom.trim() === '')) {
            ToastMsg('error', 'Debit From is required for advance salary approval');
            setApproveLoading(false);
            return;
        }

        // Resignation request payload — gets priority because some resignations may also carry an employee shape
        if (tableData.type === 'resignation') {
            const level = parseInt(selectedRequest.level, 10) || 1;
            const isFinalLevel = level === 2;
            const noticePeriod = resignationApproval.notice_period;
            const lwd = resignationApproval.last_working_day;
            const comments = resignationApproval.approval_comments;

            if (noticePeriod === '' || noticePeriod === null || noticePeriod === undefined) {
                ToastMsg('error', 'Notice Period is required');
                setApproveLoading(false);
                return;
            }
            if (!lwd) {
                ToastMsg('error', 'Last Working Day is required');
                setApproveLoading(false);
                return;
            }
            if (!comments || !comments.trim()) {
                ToastMsg('error', 'Approval comments are required');
                setApproveLoading(false);
                return;
            }

            // Pull approver IDs out of the row's nested objects — the backend serializer
            // (ResignationApprovalLogSerializer) needs a PK, not the profile object.
            const approverId =
                selectedRequest.approved_by?.id ??
                selectedRequest.approved_by ??
                null;
            const finalApproverId =
                selectedRequest.final_approved_by?.id ??
                selectedRequest.final_approved_by ??
                null;

            payload = {
                id: selectedRequest.id,
                level,
                approval_status: 'approved',
            };

            if (isFinalLevel) {
                payload.final_approved_by = finalApproverId;
                payload.final_approval_date = dayjs().format('YYYY-MM-DD');
                payload.final_notice_period = parseInt(noticePeriod, 10);
                payload.final_last_working_day = dayjs(lwd).format('YYYY-MM-DD');
                payload.final_approval_comments = comments;
            } else {
                payload.approved_by = approverId;
                payload.approval_date = dayjs().format('YYYY-MM-DD');
                payload.notice_period = parseInt(noticePeriod, 10);
                payload.last_working_day = dayjs(lwd).format('YYYY-MM-DD');
                payload.approval_comments = comments;
            }
        } else if (selectedRequest.leave_request_type || selectedRequest.leave_type_id) {
            // Leave request payload
            payload = {
                ...selectedRequest,
                approval_notes: approvalNotes,
                approval_status: selectedRequest.request_type === 'cancel request' ? 'request cancelled' : 'approved',
                company: String(selectedRequest.company) || null,
                leave_type: selectedRequest.leave_type_id || null,
            }
            delete payload.leave_type_id;
            delete payload.employee;
        } else if (selectedRequest.reimbursements_type) {
            // Reimbursement request payload — approved_amount comes from the editable input.
            // The backend promotes the status to 'partial' when approved < requested.
            const requestedAmt = Number(selectedRequest.requested_amount || 0);
            const approvedAmtRaw = reimbursementApprovedAmount === '' ? requestedAmt : Number(reimbursementApprovedAmount);
            const approvedAmt = Number.isFinite(approvedAmtRaw) ? approvedAmtRaw : 0;
            if (selectedRequest.request_type !== 'cancel request') {
                if (approvedAmt <= 0) {
                    ToastMsg('error', 'Approved amount must be greater than zero');
                    setApproveLoading(false);
                    return;
                }
                if (approvedAmt > requestedAmt) {
                    ToastMsg('error', 'Approved amount cannot exceed requested amount');
                    setApproveLoading(false);
                    return;
                }
            }
            payload = {
                id: selectedRequest.id,
                approval_status: selectedRequest.request_type === 'cancel request' ? 'request cancelled' : "approved",
                approved_amount: approvedAmt,
                description: selectedRequest.description,
                reimbursement_type: selectedRequest.reimbursements_type.id,
                requested_amount: selectedRequest.requested_amount,
                request_type: "reimbursement request",
                approval_notes: approvalNotes,
                uploaded_documents: selectedRequest.uploaded_documents || [],
            }
        } else {
            // Advance salary request payload
            payload = {
                ...selectedRequest,
                emi_period: selectedRequest.emi_period,
                approved_amount: selectedRequest.advance_requested,
                reason: selectedRequest.reason,
                advance_requested: selectedRequest.advance_requested,
                debit_from: debitFrom,
                requested_emi_period: selectedRequest.requested_emi_period,
                uploaded_documents: selectedRequest.uploaded_documents || [],
                request_type: "advance request",
                approval_status: selectedRequest.request_type === 'cancel request' ? 'request cancelled' : "approved"
            }
        }

        const response = await update(tableData.url, payload);

        if (response.status === 'success') {
            ToastMsg('success', response.message);
            setShowApproveDialog(false);
            setApprovalNotes('');
            setResignationApproval({ notice_period: '', last_working_day: null, approval_comments: '' });
            setServerTrigger(!serverTrigger);
        } else {
            ToastMsg('error', 'Failed to approve the request');
        }
        setApproveLoading(false);
    };

    // Reimbursement-only — marks an Approved/Partial reimbursement as Processed,
    // signalling that payment/accounting has been completed.
    const handleMarkProcessed = async () => {
        if (!selectedRequest) return;
        setProcessedLoading(true);
        try {
            const payload = {
                id: selectedRequest.id,
                approval_status: 'processed',
                approval_notes: processedRemarks || '',
            };
            const response = await update(REIMBURESMENT_REQUEST_URL, payload);
            if (response?.status === 'success') {
                ToastMsg('success', response.message || 'Reimbursement marked as processed');
                setShowProcessedDialog(false);
                setShowApproveDialog(false);
                setProcessedRemarks('');
                setServerTrigger(!serverTrigger);
            } else {
                ToastMsg('error', response?.message || 'Failed to mark reimbursement as processed');
            }
        } catch (err) {
            ToastMsg('error', err?.message || 'Failed to mark reimbursement as processed');
        } finally {
            setProcessedLoading(false);
        }
    };

    const handleRevertApproval = async () => {
        setRevertLoading(true);
        let payload;

        if (selectedRequest.leave_request_type || selectedRequest.leave_type_id) {
            // Leave request revert payload
            payload = {
                ...selectedRequest,
                approval_notes: revertReason,
                approval_status: "cancelled",
                request_type: "cancel request",
                company: String(selectedRequest.company) || null,
                leave_type: selectedRequest.leave_type_id || null,
            }
            delete payload.leave_type_id;
            delete payload.employee;
        } else if (selectedRequest.reimbursements_type) {
            // Reimbursement request revert payload
            payload = {
                approval_notes: revertReason,
                approval_status: "cancelled",
                id: selectedRequest.id,
                request_type: "cancel request",
                requested_amount: selectedRequest.requested_amount
            }
        } else {
            // Advance salary request revert payload
            payload = {
                emi_period: selectedRequest.emi_period,
                approved_amount: selectedRequest.approved_amount,
                reason: selectedRequest.reason,
                approval_notes: revertReason,
                advance_requested: selectedRequest.advance_requested,
                debit_from: selectedRequest.debit_from,
                requested_emi_period: selectedRequest.requested_emi_period,
                uploaded_documents: selectedRequest.uploaded_documents || [],
                id: selectedRequest.id,
                request_type: "advance request",
                approval_status: "cancelled"
            }
        }

        const response = await update(tableData.url, payload);

        if (response.status === 'success') {
            ToastMsg('success', 'Request reverted successfully');
            setShowRevertDialog(false);
            setRevertReason('');
            setServerTrigger(!serverTrigger);
        } else {
            ToastMsg('error', response.message || 'Failed to revert the request');
        }
        setRevertLoading(false);
    };

    const handleConfirmReject = async () => {
        // All reject flows require a reason; reimbursement reject is enforced both here and
        // on the backend so the rejected reason ends up in the approval log.
        if (!revertReason || !revertReason.trim()) {
            ToastMsg('error', 'Please provide a reason for rejection');
            return;
        }
        setRejectLoading(true);
        let payload;

        if (tableData.type === 'resignation') {
            // Resignation reject payload — backend requires the level-specific approver
            // ID + a non-empty comment to mark the row rejected.
            const level = parseInt(selectedRequestForReject.level, 10) || 1;
            const isFinalLevel = level === 2;
            const approverId =
                selectedRequestForReject.approved_by?.id ??
                selectedRequestForReject.approved_by ??
                null;
            const finalApproverId =
                selectedRequestForReject.final_approved_by?.id ??
                selectedRequestForReject.final_approved_by ??
                null;
            payload = {
                id: selectedRequestForReject.id,
                level,
                approval_status: 'rejected',
            };
            if (isFinalLevel) {
                payload.final_approved_by = finalApproverId;
                payload.final_approval_date = dayjs().format('YYYY-MM-DD');
                payload.final_approval_comments = revertReason || 'Rejected';
            } else {
                payload.approved_by = approverId;
                payload.approval_date = dayjs().format('YYYY-MM-DD');
                payload.approval_comments = revertReason || 'Rejected';
            }
        } else if (selectedRequestForReject.leave_request_type || selectedRequestForReject.leave_type_id) {
            // Leave request reject payload
            payload = {
                ...selectedRequestForReject,
                approval_notes: revertReason,
                approval_status: "rejected",
                request_type: "leave request",
                company: String(selectedRequestForReject.company) || null,
                leave_type: selectedRequestForReject.leave_type_id || null,
            }
            delete payload.leave_type_id;
            delete payload.employee;
        } else if (selectedRequestForReject.reimbursements_type) {
            // Reimbursement request reject payload — approval_notes carries the reject reason.
            payload = {
                reimbursement_type: selectedRequestForReject.reimbursements_type?.id || selectedRequestForReject.reimbursements_type,
                description: selectedRequestForReject.description,
                requested_amount: selectedRequestForReject.requested_amount,
                approved_amount: selectedRequestForReject.approved_amount,
                uploaded_documents: selectedRequestForReject.uploaded_documents || [],
                id: selectedRequestForReject.id,
                request_type: "reimbursement request",
                approval_status: "rejected",
                approval_notes: revertReason,
            }
        } else {
            // Advance salary request reject payload
            payload = {
                emi_period: selectedRequestForReject.emi_period,
                approved_amount: selectedRequestForReject.approved_amount,
                reason: selectedRequestForReject.reason,
                advance_requested: selectedRequestForReject.advance_requested,
                debit_from: selectedRequestForReject.debit_from,
                requested_emi_period: selectedRequestForReject.requested_emi_period,
                uploaded_documents: selectedRequestForReject.uploaded_documents || [],
                id: selectedRequestForReject.id,
                request_type: "advance request",
                approval_status: "rejected"
            }
        }

        const response = await update(tableData.url, payload);

        if (response.status === 'success') {
            ToastMsg('success', response.message);
            setShowRejectDialog(false);
            setSelectedRequestForReject(null);
            setRevertReason('');
            setServerTrigger(!serverTrigger);
        } else {
            ToastMsg('error', response.message || 'Failed to reject the request');
        }
        setRejectLoading(false);
    };

    const formatTimeToAMPM = (timeString) => {
        if (!timeString) return '';
        try {
            const [hours, minutes] = timeString.split(':');
            const hour = parseInt(hours, 10);
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const formattedHour = hour % 12 || 12;
            return `${formattedHour}:${minutes} ${ampm}`;
        } catch (error) {
            return timeString;
        }
    };

    const getRequestTypeDisplay = (type) => {
        const types = {
            'work_from_home': 'Work From Home',
            'permission': 'Permission',
            'leave': 'Leave'
        };
        return types[type] || type;
    };

    // Common status badge component
    const StatusBadge = ({ status }) => {
        const getStatusConfig = (status) => {
            const config = {
                pending: {
                    text: 'Pending',
                    className: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700'
                },
                'cancel inprogress': {
                    text: 'Cancelling',
                    className: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700'
                },
                approved: {
                    text: 'Approved',
                    className: 'bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-700'
                },
                rejected: {
                    text: 'Rejected',
                    className: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
                },
                cancelled: {
                    text: 'Cancelled',
                    className: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
                },
                partial: {
                    text: 'Partial',
                    className: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700'
                },
                processed: {
                    text: 'Processed',
                    className: 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700'
                }
            };

            return config[status] || {
                text: status?.charAt(0).toUpperCase() + status?.slice(1) || 'Pending',
                className: 'bg-blue-100 text-blue-700 border-blue-200'
            };
        };

        const statusConfig = getStatusConfig(status);

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${statusConfig.className}`}>
                {statusConfig.text}
            </span>
        );
    };

    // Common action buttons component
    const ActionButtons = ({ row, onApprove, onReject, onViewLog, onRevert, showRevert = true, type }) => {

        const handleApprove = () => {
            setSelectedRequest(row);
            setShowApproveDialog(true);
        };

        const handleReject = () => {
            setSelectedRequestForReject(row);
            setShowRejectDialog(true);
        };

        const handleApproveLog = () => {
            setSelectedRequest(row);
            setShowApprovalLog(true);
        }

        const handleRevertApproval = () => {
            setSelectedRequest(row);
            setShowRevertDialog(true);
        }

        const handleProcessed = () => {
            setSelectedRequest(row);
            setShowProcessedDialog(true);
        }

        // Check if the request date has passed (for revert button and approve/reject buttons)
        const isRequestDatePassed = () => {
            if (!row.from_date) return false;

            try {
                const requestDate = new Date(row.from_date);
                const today = new Date();
                requestDate.setHours(0, 0, 0, 0);
                today.setHours(0, 0, 0, 0);
                return requestDate < today;
            } catch (error) {
                return false;
            }
        };

        // Only disable for leave requests when date has passed
        const isLeaveRequest = row.leave_request_type === 'leave' || row.approval_type === 'Leave';

        const shouldDisableRevert = isRequestDatePassed();

        // Disable approve/reject buttons when status is not pending
        const shouldDisableApproveReject = row.status !== 'pending' && row.status !== 'cancelled' && row.status !== 'cancel inprogress';

        // Additional condition for leave requests: also disable if date has passed
        const shouldDisableLeaveApproveReject = isLeaveRequest && isRequestDatePassed();

        // Final disable condition: either status is not pending OR (for leave requests, date has passed)
        const finalDisableApproveReject = shouldDisableApproveReject;

        const revertTitle = shouldDisableRevert
            ? "Cannot revert - Request date has passed"
            : "Revert Approval";

        const approveRejectTitle = finalDisableApproveReject || (shouldDisableLeaveApproveReject)
            ? row.status !== 'pending'
                ? "Cannot approve/reject - Request already processed"
                : "Cannot approve/reject - Request date has passed"
            : "";

        return (
            <div className="flex gap-3 items-center justify-end">
                {(row.status === 'pending' || row.status === 'cancel inprogress' ) && (
                    <>
                        <Button
                            variant="ghost"
                            size="xs"
                            onClick={handleApprove}
                            disabled={finalDisableApproveReject}
                            className={`h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 ${finalDisableApproveReject
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                : 'bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100 hover:text-emerald-700'
                                }`}
                            title={finalDisableApproveReject ? approveRejectTitle : "Approve Request"}
                        >
                            <ThumbsUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="xs"
                            onClick={handleReject}
                            // disabled={finalDisableApproveReject}
                            className={`h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 ${finalDisableApproveReject
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                : 'bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 hover:text-rose-700'
                                }`}
                            title={finalDisableApproveReject ? approveRejectTitle : "Reject Request"}
                        >
                            <ThumbsDown className="h-3.5 w-3.5" />
                        </Button>
                    </>
                )}
                {(row.status === 'approved' || row.status === 'rejected' || row.status === 'partial' || row.status === 'processed' || row.status === 'cancel inprogress' || row.status === 'cancelled') && (
                    <>
                        <Button
                            variant="ghost"
                            size="xs"
                            onClick={handleApproveLog}
                            disabled={!row.approval_log || row.approval_log.length === 0}
                            className={`h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 ${!row.approval_log || row.approval_log.length === 0
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                : 'bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100 hover:text-amber-700'
                                }`}
                            title={(!row.approval_log || row.approval_log.length === 0) ? "No approval log available" : "View Approval Log"}
                        >
                            <History className="h-3.5 w-3.5" />
                        </Button>

                        {type === 'reimbursement' && (row.status === 'approved' || row.status === 'partial') && (
                            <Button
                                variant="ghost"
                                size="xs"
                                onClick={handleProcessed}
                                className="h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100 hover:text-emerald-700"
                                title="Mark as Processed"
                            >
                                <CheckCheck className="h-3.5 w-3.5" />
                            </Button>
                        )}

                        {showRevert && (
                            (type === 'reimbursement'
                                ? row.status === 'processed'
                                : (row.status === 'approved' || row.status === 'rejected' || row.status === 'partial')
                            ) && (
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={!shouldDisableRevert ? handleRevertApproval : handleRevertApproval}
                                    className={`h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 ${shouldDisableRevert
                                        // ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                        ? 'bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 hover:text-blue-700'
                                        : 'bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 hover:text-blue-700'
                                        }`}
                                    title={revertTitle}
                                >
                                    <Rewind className="h-3.5 w-3.5" />
                                </Button>
                            )
                        )}
                    </>
                )}
            </div>
        );
    };

    // Leave approval columns
    const leaveRequestColumns = [
        {
            accessor: 'employee', Header: 'Employee Name', sortable: true, filterable: true, fixed: true, width: '180px',
            Cell: ({ value, row }) => {
                const record = row;
                let empDetails = {
                    ...record?.request_from,
                    designation_id: record?.request_from?.designation
                        ? {
                            id: record?.request_from.designation.id,
                            designation_name: record?.request_from.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: record?.request_from?.employee_name || "-",
                    id: record?.request_from?.employee_id || "-",
                };
                return <EmployeeDetails details={empDetails} size="xs" onClick={() => {
                    setSelectedRequest(row);
                    setShowApproveDialog(true);
                }} />;
            }
        },
        {
            accessor: 'leave_request_type', Header: 'Request Type', filterable: true, width: '160px',
            Cell: ({ value }) => {
                if (!value) {
                    return (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600">
                            Not Mentioned
                        </span>
                    );
                }

                const getRequestTypeConfig = (type) => {
                    const config = {
                        work_from_home: {
                            text: 'Work From Home',
                            className: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border-purple-300 dark:border-purple-700',
                            icon: '🏠'
                        },
                        permission: {
                            text: 'Permission',
                            className: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-700',
                            icon: '⏱️'
                        },
                        leave: {
                            text: 'Leave',
                            className: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700',
                            icon: '🌴'
                        }
                    };

                    const defaultConfig = {
                        text: type.split('_').map(word =>
                            word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' '),
                        className: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600',
                        icon: '📝'
                    };

                    return config[type] || defaultConfig;
                };

                const typeConfig = getRequestTypeConfig(value);

                return (
                    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border ${typeConfig.className}`}>
                        <span className="text-xs">{typeConfig.icon}</span>
                        {typeConfig.text}
                    </span>
                );
            }
        },
        {
            accessor: 'duration',
            Header: 'Duration',
            sortable: true,
            filterable: true,
            filterType: 'range',
            width: '280px',
            Cell: ({ row }) => {
                const startDate = row.from_date || row.startDate;
                const endDate = row.to_date || row.endDate;
                const startTime = row.start_time;
                const endTime = row.end_time;
                const isPermission = row.leave_request_type === 'permission';

                const formatDate = (dateString) => {
                    if (!dateString) return '';
                    try {
                        const [year, month, day] = dateString.split('-');
                        return `${day}-${month}-${year}`;
                    } catch (error) {
                        return dateString; // Return original if parsing fails
                    }
                };

                const formattedStartDate = formatDate(startDate);
                const formattedEndDate = formatDate(endDate);

                // Same day with times
                if (isPermission && startDate === endDate && startTime && endTime) {
                    return (
                        <div className="flex flex-col gap-2">
                            <span className="text-[0.75rem] font-medium text-gray-700 dark:text-gray-300">{formattedStartDate}</span>
                            <div className="flex items-center gap-3">
                                <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-2 py-1 rounded-full text-[0.65rem] font-medium border border-blue-200 dark:border-blue-700">
                                    {formatTimeToAMPM(startTime)}
                                </span>
                                <span className="text-[0.65rem] text-gray-400 dark:text-gray-500">-</span>
                                <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 px-2 py-1 rounded-full text-[0.65rem] font-medium border border-orange-200 dark:border-orange-700">
                                    {formatTimeToAMPM(endTime)}
                                </span>
                            </div>
                        </div>
                    );
                }

                // Multi-day request
                if (startDate && endDate && startDate !== endDate) {
                    return (
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                                <span className="text-[0.75rem] font-medium text-gray-700 dark:text-gray-300">{formattedStartDate}</span>
                                {isPermission && startTime && (
                                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-1.5 py-0.5 rounded text-[0.6rem] font-medium">
                                        {formatTimeToAMPM(startTime)}
                                    </span>
                                )}
                                <span className="text-[0.65rem] text-gray-400 dark:text-gray-500">to</span>
                                <span className="text-[0.75rem] font-medium text-gray-700 dark:text-gray-300">{formattedEndDate}</span>
                                {isPermission && endTime && (
                                    <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 px-1.5 py-0.5 rounded text-[0.6rem] font-medium">
                                        {formatTimeToAMPM(endTime)}
                                    </span>
                                )}
                            </div>
                            {!isPermission && row.period && row.period !== '-' && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border border-purple-300 dark:border-purple-700 w-fit">
                                    <span className="text-xs">📅</span>
                                    {row.period.replace('_', ' ')}
                                </span>
                            )}
                        </div>
                    );
                }

                // Single day
                if (startDate) {
                    return (
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                                <span className="text-[0.75rem] font-medium text-gray-700 dark:text-gray-300">{formattedStartDate}</span>
                                {isPermission && startTime && (
                                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-2 py-1 rounded-full text-[0.75rem] font-medium border border-blue-200 dark:border-blue-700">
                                        {formatTimeToAMPM(startTime)}
                                    </span>
                                )}
                            </div>
                            {!isPermission && row.period && row.period !== '-' && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border border-purple-300 dark:border-purple-700 w-fit">
                                    <span className="text-xs">📅</span>
                                    {row.period.replace('_', ' ')}
                                </span>
                            )}
                        </div>
                    );
                }

                return '-';
            }
        },
        {
            accessor: 'status', Header: 'Status', sortable: true, filterable: true, filterType: 'requestStatus', width: '140px',
            Cell: ({ value }) => <StatusBadge status={value} />
        },
        {
            accessor: 'approved_by', Header: 'Approved By', sortable: true, filterable: true, width: '180px',
            Cell: ({ value, row }) => {
                const record = row;
                if (!record?.approved_by || !record.approved_by.employee_id || record.approved_by.employee_id === null) {
                    return (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600">
                            Not Approved
                        </span>
                    );
                }

                let empDetails = {
                    ...record?.approved_by,
                    designation_id: record?.approved_by?.designation
                        ? {
                            id: record?.approved_by.designation.id,
                            designation_name: record?.approved_by.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: record?.approved_by?.employee_name || "-",
                    id: record?.approved_by?.employee_id || "-",
                };

                return <EmployeeDetails details={empDetails} size="xs" />;
            }
        },
        {
            accessor: 'reason', Header: 'Reason', sortable: false, width: '200px', Cell: ({ value }) => value ? truncateText(value, 20, "300px", "balance") : ""
        },
        {
            Header: 'Action', fixed: true, align: 'right',
            Cell: ({ row }) => <ActionButtons row={row} />
        }
    ];

    // Reimbursement columns
    const reimbursementColumns = [
        {
            accessor: 'employee', Header: 'Employee Name', sortable: true, filterable: true, fixed: true,
            Cell: ({ value, row }) => {
                const record = row;
                let empDetails = {
                    ...record?.request_from,
                    designation_id: record?.request_from?.designation
                        ? {
                            id: record?.request_from.designation.id,
                            designation_name: record?.request_from.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: record?.request_from?.employee_name || "-",
                    id: record?.request_from?.employee_id || "-",
                };
                return <EmployeeDetails details={empDetails} size="xs" onClick={() => {
                    setSelectedRequest(row);
                    setShowApproveDialog(true);
                }} />;
            }
        },
        { accessor: 'date', Header: 'Date', sortable: true, filterable: true, filterType: 'range', },
        { accessor: 'amount', Header: 'Amount', sortable: true, filterable: true, Cell: ({ value }) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value) },
        { accessor: 'reimbursements_type', Header: 'Category', sortable: true, Cell: ({ value }) => value?.name || '-' },
        {
            accessor: 'request_notes', Header: 'Request Note', sortable: false, Cell: ({ value }) => value ? truncateText(value, 20, "300px", "balance") : "-"
        },
        {
            accessor: 'status', Header: 'Status', sortable: true, filterable: true, filterType: 'requestStatus', width: '140px',
            Cell: ({ value }) => <StatusBadge status={value} />
        },
        {
            Header: 'Action', fixed: true, align: 'right',
            Cell: ({ row }) => <ActionButtons row={row} type="reimbursement" />
        }
    ];

    // Advance Salary columns
    const advanceSalaryColumns = [
        {
            accessor: 'employee', Header: 'Employee Name', sortable: true, filterable: true, fixed: true,
            Cell: ({ value, row }) => {
                const record = row;
                let empDetails = {
                    ...record?.request_from,
                    designation_id: record?.request_from?.designation
                        ? {
                            id: record?.request_from.designation.id,
                            designation_name: record?.request_from.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: record?.request_from?.employee_name || "-",
                    id: record?.request_from?.employee_id || "-",
                };
                return <EmployeeDetails details={empDetails} size="xs" onClick={() => {
                    setSelectedRequest(row);
                    setShowApproveDialog(true);
                }} />;
            }
        },
        { accessor: 'date', Header: 'Request Date', sortable: true, filterable: true, filterType: 'date', },
        { accessor: 'advance_requested', Header: 'Amount', sortable: true, filterable: true, Cell: ({ value }) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value) },
        {
            accessor: 'reason', Header: 'Reason', sortable: false, Cell: ({ value }) => value ? truncateText(value, 20, "300px", "balance") : ""
        },
        {
            accessor: 'status', Header: 'Status', sortable: true, width: '140px', filterable: true, filterType: 'requestStatus',
            Cell: ({ value }) => <StatusBadge status={value} />
        },
        {
            Header: 'Action', fixed: true, align: 'right',
            Cell: ({ row }) => <ActionButtons row={row} />
        }
    ];

    // Resignation columns
    const resignationrequestColumns = [
        {
            accessor: 'employee', Header: 'Employee Name', sortable: true, filterable: true, fixed: true,
            Cell: ({ value, row }) => {
                const record = row;
                let empDetails = {
                    ...record?.employee,
                    designation_id: record?.employee?.designation
                        ? {
                            id: record?.employee.designation.id,
                            designation_name: record?.employee.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: record?.employee?.employee_name || "-",
                    id: record?.employee?.employee_id || "-",
                };
                return <EmployeeDetails details={empDetails} size="xs" onClick={() => {
                    setSelectedRequest(row);
                    setShowApproveDialog(true);
                }} />;
            }
        },
        { accessor: 'request_date', Header: 'Request Date', sortable: true, Cell: ({ value }) => value ? String(value).split('T')[0] : '-' },
        {
            accessor: 'reason', Header: 'Reason', sortable: false, Cell: ({ value }) => value ? truncateText(value, 20, "300px", "balance") : ""
        },
        {
            accessor: 'status', Header: 'Status', sortable: true, width: '140px',
            Cell: ({ value }) => <StatusBadge status={value} />
        },
        {
            Header: 'Action', fixed: true, align: 'right',
            Cell: ({ row }) => {
                const isApproved = row.status === 'approved' || row.approval_status === 'approved';
                return (
                    <div className="flex items-center justify-end gap-2">
                        <ActionButtons row={row} showRevert={false} />
                        {isApproved && (
                            <Button
                                variant="ghost"
                                size="xs"
                                onClick={() => navigate('/employee/resignationrequest/process', {
                                    state: { resignationId: row.id, stepKey: 1 },
                                })}
                                className="h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100 hover:text-emerald-700"
                                title="Run Exit Process"
                            >
                                <ListChecks className="h-3.5 w-3.5" />
                            </Button>
                        )}
                    </div>
                );
            },
        }
    ];

    const tableData = useMemo(() => {
        switch (activeTab) {
            case 'leave':
                return { data: leaveRequests, setData: setLeaveRequests, columns: leaveRequestColumns, url: LEAVE_REQUEST_URL, type: 'leave' };
            case 'reimbursement':
                return { data: reimbursements, setData: setReimbursements, columns: reimbursementColumns, url: REIMBURESMENT_REQUEST_URL, type: 'reimbursement' };
            case 'advance':
                return { data: advanceSalary, setData: setAdvanceSalary, columns: advanceSalaryColumns, url: ADVANCE_REQUEST_URL, type: 'advance' };
            case 'resign':
                return { data: resignationrequest, setData: setResignationrequest, columns: resignationrequestColumns, url: RESIGNATION_REQUEST_URL, type: 'resignation' };
            default:
                return { data: [], setData: () => { }, columns: [], type: '' };
        }
    }, [activeTab, leaveRequests, reimbursements, advanceSalary, resignationrequest]);

    // Render appropriate dialog content based on request type
    const renderDialogContent = () => {
        if (!selectedRequest) return null;

        const requestType = tableData.type;

        switch (requestType) {
            case 'leave':
                return renderLeaveRequestDialog();
            case 'reimbursement':
                return renderReimbursementDialog();
            case 'advance':
                return renderAdvanceSalaryDialog();
            case 'resignation':
                return renderResignationDialog();
            default:
                return renderDefaultDialog();
        }
    };

    const renderLeaveRequestDialog = () => (
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {/* Header Status Bar */}
            <div className="flex items-center justify-between p-2 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${selectedRequest.status === 'pending' ? 'bg-yellow-500' :
                        selectedRequest.status === 'approved' ? 'bg-green-500' :
                            'bg-red-500'
                        }`}></div>
                    <div>
                        <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Leave Request Review</h3>
                        <p className="text-xs text-gray-600 dark:text-gray-300">Please review all details carefully</p>
                    </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-medium border ${selectedRequest.status === 'pending' ? 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700' :
                    selectedRequest.status === 'approved' ? 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700' :
                        'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
                    }`}>
                    {selectedRequest.status?.charAt(0).toUpperCase() + selectedRequest.status?.slice(1)}
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">

                {/* Column 1: Employee & Request Info */}
                <div className="space-y-4">
                    {/* Employee Card - Blue Theme */}
                    <div className="bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-900/10 dark:to-sky-900/10 rounded-lg border border-blue-200 dark:border-blue-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Employee Information</h4>
                        </div>
                        <div className="space-y-2 text-xs">
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Name:</span>
                                <span className="font-semibold text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.employee_name || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Employee ID:</span>
                                <span className="font-semibold text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.employee_id || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Department:</span>
                                <span className="font-semibold text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.department?.name || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Designation:</span>
                                <span className="font-semibold text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.designation?.name || '-'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Request Details Card - Purple Theme */}
                    <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/10 dark:to-violet-900/10 rounded-lg border border-purple-200 dark:border-purple-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Request Details</h4>
                        </div>
                        <div className="space-y-2 text-xs">
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600 dark:text-gray-300">Type:</span>
                                <span className={`px-2 py-1 rounded text-xs font-medium border ${selectedRequest.leave_request_type === 'work_from_home' ? 'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700' :
                                    selectedRequest.leave_request_type === 'permission' ? 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700' :
                                        'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700'
                                    }`}>
                                    {getRequestTypeDisplay(selectedRequest.leave_request_type)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Leave Type:</span>
                                <span className="font-semibold text-gray-800 dark:text-white">{selectedRequest.leave_type || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Requested On:</span>
                                <span className="font-semibold text-gray-800 dark:text-white">{selectedRequest.requested_date || '-'}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Column 2: Duration & Documents */}
                <div className="space-y-4">
                    {/* Duration Card - Green Theme */}
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/10 dark:to-emerald-900/10 rounded-lg border border-green-200 dark:border-green-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Duration & Timing</h4>
                        </div>
                        {selectedRequest.leave_request_type === 'permission' ? (
                            // Permission: Show time-based layout
                            <div className="grid grid-cols-2 gap-3">
                                {/* Start Time - Blue Accent */}
                                <div className="text-center p-3 bg-gradient-to-b from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/30 rounded-lg border border-blue-200 dark:border-blue-600">
                                    <div className="flex items-center justify-center gap-1 mb-1">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                        <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300">Start Time</h5>
                                    </div>
                                    <div className="text-xs font-semibold text-gray-800 dark:text-white mb-1">
                                        {selectedRequest.from_date}
                                    </div>
                                    {selectedRequest.start_time && selectedRequest.start_time !== '-' && (
                                        <div className="bg-white dark:bg-gray-800 px-2 py-1 rounded border border-blue-200 dark:border-blue-600">
                                            <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                                                {formatTimeToAMPM(selectedRequest.start_time)}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* End Time - Orange Accent */}
                                <div className="text-center p-3 bg-gradient-to-b from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/30 rounded-lg border border-orange-200 dark:border-orange-600">
                                    <div className="flex items-center justify-center gap-1 mb-1">
                                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                                        <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300">End Time</h5>
                                    </div>
                                    <div className="text-xs font-semibold text-gray-800 dark:text-white mb-1">
                                        {selectedRequest.to_date}
                                    </div>
                                    {selectedRequest.end_time && selectedRequest.end_time !== '-' && (
                                        <div className="bg-white dark:bg-gray-800 px-2 py-1 rounded border border-orange-200 dark:border-orange-600">
                                            <span className="text-xs font-medium text-orange-700 dark:text-orange-300">
                                                {formatTimeToAMPM(selectedRequest.end_time)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            // Non-permission: Show period layout
                            <div className="space-y-3">
                                {/* Date Range */}
                                <div className="grid grid-cols-2 gap-3">
                                    {/* Start Date */}
                                    <div className="text-center p-3 bg-gradient-to-b from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/30 rounded-lg border border-blue-200 dark:border-blue-600">
                                        <div className="flex items-center justify-center gap-1 mb-1">
                                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                            <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300">Start Date</h5>
                                        </div>
                                        <div className="text-xs font-semibold text-gray-800 dark:text-white">
                                            {selectedRequest.from_date}
                                        </div>
                                    </div>

                                    {/* End Date */}
                                    <div className="text-center p-3 bg-gradient-to-b from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/30 rounded-lg border border-orange-200 dark:border-orange-600">
                                        <div className="flex items-center justify-center gap-1 mb-1">
                                            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                                            <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300">End Date</h5>
                                        </div>
                                        <div className="text-xs font-semibold text-gray-800 dark:text-white">
                                            {selectedRequest.to_date}
                                        </div>
                                    </div>
                                </div>

                                {/* Period Type */}
                                {selectedRequest.period && selectedRequest.period !== '-' && (
                                    <div className="text-center p-3 bg-gradient-to-b from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/30 rounded-lg border border-purple-200 dark:border-purple-600">
                                        <div className="flex items-center justify-center gap-1 mb-1">
                                            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                            <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300">Period Type</h5>
                                        </div>
                                        <div className="text-xs font-semibold text-gray-800 dark:text-white capitalize">
                                            {selectedRequest.period.replace('_', ' ')}
                                        </div>
                                        {selectedRequest.total_duration && selectedRequest.total_duration !== 'NaT' && (
                                            <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                                Duration: {selectedRequest.total_duration}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Documents Card - Indigo Theme */}
                    {selectedRequest.uploaded_documents && selectedRequest.uploaded_documents.length > 0 && (
                        <div className="bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-900/10 dark:to-violet-900/10 rounded-lg border border-indigo-200 dark:border-indigo-700 p-4 shadow-sm">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                                <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Attached Documents</h4>
                                <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded text-xs">
                                    {selectedRequest.uploaded_documents.length}
                                </span>
                            </div>
                            <div className="space-y-2">
                                {selectedRequest.uploaded_documents.map((docUrl, index) => {
                                    const getFileName = (url) => {
                                        try {
                                            return new URL(url).pathname.split('/').pop() || `Document ${index + 1}`;
                                        } catch {
                                            return `Document ${index + 1}`;
                                        }
                                    };

                                    const isImage = (url) => {
                                        const fileName = getFileName(url);
                                        const extension = fileName.split('.').pop()?.toLowerCase();
                                        return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension);
                                    };

                                    const fileName = getFileName(docUrl);
                                    const fileIsImage = isImage(docUrl);

                                    return (
                                        <div key={index} className="flex items-center justify-between p-2 bg-white/50 dark:bg-gray-800/50 rounded border border-gray-200 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-700 transition-colors">
                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <span className="text-base">
                                                    {fileIsImage ? '🖼️' : '📄'}
                                                </span>
                                                <span className="text-xs font-medium text-gray-800 dark:text-white truncate flex-1">
                                                    {fileName}
                                                </span>
                                            </div>
                                            {fileIsImage ? (
                                                <Image
                                                    width={80}
                                                    src={docUrl}
                                                    alt={`Preview of ${fileName}`}
                                                    className="rounded cursor-pointer"
                                                    preview={{
                                                        mask: <Eye className="h-3 w-3" />,
                                                    }}
                                                />
                                            ) : (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        const link = document.createElement('a');
                                                        link.href = docUrl;
                                                        link.download = fileName;
                                                        document.body.appendChild(link);
                                                        link.click();
                                                        document.body.removeChild(link);
                                                    }}
                                                    className="h-6 px-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 text-xs font-medium transition-colors"
                                                >
                                                    View
                                                </Button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* Column 3: Reason & Notes */}
                <div className="space-y-4">
                    {/* Reason Card - Amber Theme */}
                    <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/10 dark:to-yellow-900/10 rounded-lg border border-amber-200 dark:border-amber-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Reason</h4>
                        </div>
                        <div className="bg-white/50 dark:bg-gray-800/50 rounded border border-amber-200 dark:border-amber-600 min-h-[100px] max-h-[120px] overflow-y-auto p-3">
                            <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                                {selectedRequest.reason || 'No reason provided for this request.'}
                            </p>
                        </div>
                    </div>

                    {/* Approval Notes Card - Gray Theme */}
                    <div className="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800 dark:to-slate-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-gray-600 dark:bg-gray-400 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Approval Notes</h4>
                            <span className="text-xs text-gray-500 dark:text-gray-400">(Optional)</span>
                        </div>
                        <Textarea
                            value={approvalNotes}
                            onChange={(e) => setApprovalNotes(e.target.value)}
                            placeholder="Add your comments or notes for this approval decision..."
                            rows={3}
                            showCharCount={true}
                            maxLength={250}
                            className={cn(
                                "w-full rounded border transition-colors duration-200 text-xs",
                                "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-1 focus:ring-sidebar-primary/20 dark:focus:border-sidebar-primary dark:focus:ring-sidebar-primary/30",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            These notes will be visible to the employee and other approvers.
                        </p>
                    </div>
                </div>
            </div>

            {/* Information Footer - Blue Theme */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700 p-3">
                <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center mt-0.5">
                        <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div className="flex-1">
                        <p className="text-xs font-medium text-blue-800 dark:text-blue-300">Important Information</p>
                        <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                            Approving this request will automatically notify the employee and update their leave balance.
                            Rejecting will require providing a reason.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderReimbursementDialog = () => (
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {/* Header Status Bar */}
            <div className="flex items-center justify-between p-2 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${selectedRequest.status === 'pending' ? 'bg-yellow-500' :
                        selectedRequest.status === 'processed' ? 'bg-emerald-500' :
                            selectedRequest.status === 'approved' || selectedRequest.status === 'partial' ? 'bg-green-500' :
                                'bg-red-500'
                        }`}></div>
                    <div>
                        <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Reimbursement Request Review</h3>
                        <p className="text-xs text-gray-600 dark:text-gray-300">Please review all details carefully</p>
                    </div>
                </div>
                <StatusBadge status={selectedRequest.status} />
            </div>

            {/* Dedicated "Mark as Processed" CTA — visible only when the
                reimbursement has been approved/partial and is awaiting
                finance processing. Lives inside the dialog body (not the
                footer) so it doesn't conflict with the Approve save button. */}
            {(selectedRequest.status === 'approved' || selectedRequest.status === 'partial') && (
                <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-emerald-200 dark:border-emerald-700 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20">
                    <div className="flex items-center gap-2 min-w-0">
                        <CheckCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                        <div className="min-w-0">
                            <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-200">
                                Ready for payment processing
                            </p>
                            <p className="text-[11px] text-emerald-700 dark:text-emerald-300 truncate">
                                Approved Rs {selectedRequest.approved_amount ?? selectedRequest.requested_amount ?? 0}.00 — mark as processed once payment / accounting is complete.
                            </p>
                        </div>
                    </div>
                    <Button
                        size="sm"
                        onClick={() => setShowProcessedDialog(true)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow border border-emerald-700 shrink-0"
                    >
                        <CheckCheck className="w-4 h-4 mr-1" />
                        Mark as Processed
                    </Button>
                </div>
            )}

            {/* Processed receipt summary — once the reimbursement has been
                processed, surface who processed it and when so the reviewer
                doesn't have to open the approval log. */}
            {selectedRequest.status === 'processed' && (
                <div className="flex items-center gap-3 p-3 rounded-lg border border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20">
                    <BadgeCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                    <div className="min-w-0 text-xs text-emerald-800 dark:text-emerald-200">
                        Processed
                        {selectedRequest.processed_by_name ? ` by ${selectedRequest.processed_by_name}` : ''}
                        {selectedRequest.processed_date ? ` on ${dayjs(selectedRequest.processed_date).format('DD MMM YYYY, hh:mm A')}` : ''}
                        {selectedRequest.processed_remarks ? ` — ${selectedRequest.processed_remarks}` : ''}
                    </div>
                </div>
            )}

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Employee & Request Info */}
                <div className="space-y-4">
                    <div className="bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-900/10 dark:to-sky-900/10 rounded-lg border border-blue-200 dark:border-blue-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Employee Information</h4>
                        </div>
                        <div className="space-y-2 text-xs">
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Name:</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.employee_name || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Employee ID:</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.employee_id || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Department:</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.department?.name || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Designation:</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.designation?.name || '-'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/10 dark:to-violet-900/10 rounded-lg border border-purple-200 dark:border-purple-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Reimbursement Details</h4>
                        </div>
                        <div className="space-y-2 text-xs">
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Category:</span>
                                <span className="font-medium text-gray-800 dark:text-white">{selectedRequest.reimbursements_type?.name || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Requested Amount:</span>
                                <span className="font-medium text-gray-800 dark:text-white">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(selectedRequest.requested_amount ?? selectedRequest.amount ?? 0)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Date:</span>
                                <span className="font-medium text-gray-800 dark:text-white">{selectedRequest.date || '-'}</span>
                            </div>
                            {selectedRequest.reimbursements_type?.expense_category === 'travel' && (
                                <>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-300">From:</span>
                                        <span className="font-medium text-gray-800 dark:text-white">{selectedRequest.from_location || '-'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-300">To:</span>
                                        <span className="font-medium text-gray-800 dark:text-white">{selectedRequest.to_location || '-'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-300">Mode:</span>
                                        <span className="font-medium text-gray-800 dark:text-white capitalize">{(selectedRequest.mode_of_transport || '-').replace('_', ' ')}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-300">Distance:</span>
                                        <span className="font-medium text-gray-800 dark:text-white">{selectedRequest.distance_km ? `${selectedRequest.distance_km} km` : '-'}</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Approved Amount input — defaults to requested. Lowering it triggers Partial status. */}
                    {selectedRequest.status === 'pending' && selectedRequest.request_type !== 'cancel request' && (
                        <div className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/10 dark:to-green-900/10 rounded-lg border border-emerald-200 dark:border-emerald-700 p-4 shadow-sm">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                                <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Approved Amount</h4>
                            </div>
                            <Input
                                type="number"
                                min={0}
                                max={selectedRequest.requested_amount}
                                value={reimbursementApprovedAmount}
                                onChange={(e) => setReimbursementApprovedAmount(e.target.value)}
                                placeholder="Enter approved amount"
                                className="w-full"
                            />
                            {(() => {
                                const requested = Number(selectedRequest.requested_amount || 0);
                                const approvedNum = Number(reimbursementApprovedAmount);
                                if (!Number.isFinite(approvedNum) || approvedNum <= 0) {
                                    return <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Defaults to the requested amount. Lower it to approve partially.</p>;
                                }
                                if (approvedNum < requested) {
                                    return <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">This will be marked as <strong>Partial</strong> approval.</p>;
                                }
                                if (approvedNum > requested) {
                                    return <p className="text-xs text-red-600 dark:text-red-400 mt-2">Approved amount cannot exceed the requested amount.</p>;
                                }
                                return <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-2">Full amount will be approved.</p>;
                            })()}
                        </div>
                    )}

                    {/* attachement preview in a carosel format */}
                    {selectedRequest.uploaded_documents && selectedRequest.uploaded_documents.length > 0 && (
                        <div className="bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-900/10 dark:to-violet-900/10 rounded-xl border border-indigo-200 dark:border-indigo-700 p-4 shadow-lg">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full"></div>
                                    <h4 className="text-sm font-semibold text-gray-800 dark:text-white">Attached Documents</h4>
                                    <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full text-xs font-medium">
                                        {selectedRequest.uploaded_documents.length}
                                    </span>
                                </div>
                            </div>

                            <div className="relative">
                                {/* Navigation Arrows */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const container = e.currentTarget.closest('.relative').querySelector('.carousel-container');
                                        container.scrollBy({ left: -300, behavior: 'smooth' });
                                    }}
                                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full w-8 h-8 flex items-center justify-center shadow-md hover:shadow-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                                </button>

                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const container = e.currentTarget.closest('.relative').querySelector('.carousel-container');
                                        container.scrollBy({ left: 300, behavior: 'smooth' });
                                    }}
                                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full w-8 h-8 flex items-center justify-center shadow-md hover:shadow-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronRight className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                                </button>

                                {/* Carousel Container */}
                                <div className="carousel-container flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth pb-3 px-1" style={{ scrollbarWidth: 'none' }}>
                                    {selectedRequest.uploaded_documents.map((docUrl, index) => {
                                        const getFileName = (url) => {
                                            try {
                                                return new URL(url).pathname.split('/').pop() || `Document ${index + 1}`;
                                            } catch {
                                                return `Document ${index + 1}`;
                                            }
                                        };

                                        const isImage = (url) => {
                                            const fileName = getFileName(url);
                                            const extension = fileName.split('.').pop()?.toLowerCase();
                                            return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(extension);
                                        };

                                        const fileName = getFileName(docUrl);
                                        const fileIsImage = isImage(docUrl);
                                        const isPDF = fileName.toLowerCase().endsWith('.pdf');

                                        const handleDownload = (e) => {
                                            e.stopPropagation();
                                            const link = document.createElement('a');
                                            link.href = docUrl;
                                            link.download = fileName;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                        };

                                        return (
                                            <div
                                                key={index}
                                                className="flex-none w-56 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 hover:border-indigo-300 dark:hover:border-indigo-500 overflow-hidden group"
                                            >
                                                {/* File Preview Area */}
                                                <div className="h-32 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center relative overflow-hidden">
                                                    {fileIsImage ? (
                                                        <div className="relative w-full h-full">
                                                            <Image
                                                                src={docUrl}
                                                                alt={`Preview of ${fileName}`}
                                                                fill
                                                                className="object-cover group-hover:scale-105 transition-transform duration-300"
                                                                sizes="(max-width: 224px) 100vw, 224px"
                                                            />
                                                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                                                        </div>
                                                    ) : (
                                                        <div className="p-4 text-center">
                                                            <div className="w-12 h-12 mx-auto mb-2 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center shadow-sm">
                                                                <span className="text-2xl">
                                                                    {isPDF ? '📕' : '📄'}
                                                                </span>
                                                            </div>
                                                            <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                                                {isPDF ? 'PDF Document' : 'File'}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Preview Overlay with Download Button */}
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                        <div className="flex gap-2">
                                                            {fileIsImage ? (
                                                                <>
                                                                    {/* Image Preview Button */}
                                                                    <Image
                                                                        width={200}
                                                                        src={docUrl}
                                                                        alt={fileName}
                                                                        className="hidden"
                                                                        preview={{
                                                                            mask: (
                                                                                <Button
                                                                                    type="text"
                                                                                    className="text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm border-0 flex items-center gap-2 px-4 py-2 rounded-lg"
                                                                                >
                                                                                    <Eye className="w-4 h-4" />
                                                                                </Button>
                                                                            ),
                                                                        }}
                                                                    />
                                                                </>
                                                            ) : (
                                                                <Button
                                                                    type="text"
                                                                    className="text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm border-0 flex items-center gap-2 px-4 py-2 rounded-lg"
                                                                    icon={<Eye className="w-4 h-4" />}
                                                                    onClick={handleDownload}
                                                                >
                                                                    <span className="text-sm font-medium">View</span>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* File Info with Download Button */}
                                                <div className="p-3">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex items-center gap-1.5 mb-1">
                                                                <span className="text-xs">
                                                                    {fileIsImage ? '🖼️' : (isPDF ? '📕' : '📄')}
                                                                </span>
                                                                <span className="text-xs font-medium text-gray-800 dark:text-white truncate">
                                                                    {fileName}
                                                                </span>
                                                            </div>
                                                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                                {fileIsImage ? 'Image' : isPDF ? 'PDF Document' : 'Document'}
                                                            </div>
                                                        </div>
                                                        {/* Download Button for Images */}
                                                        {
                                                            fileIsImage && (
                                                                <Button
                                                                    type="text"
                                                                    variant='ghost'
                                                                    onClick={handleDownload}
                                                                >
                                                                    <Download className="w-4 h-4" />
                                                                </Button>
                                                            )
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Carousel Dots Indicator */}
                                {selectedRequest.uploaded_documents.length > 1 && (
                                    <div className="flex justify-center gap-1.5 mt-4">
                                        {[...Array(Math.min(5, selectedRequest.uploaded_documents.length))].map((_, i) => (
                                            <button
                                                key={i}
                                                className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
                                                onClick={(e) => {
                                                    const container = e.currentTarget.closest('.relative').querySelector('.carousel-container');
                                                    container.scrollTo({ left: i * 300, behavior: 'smooth' });
                                                }}
                                            />
                                        ))}
                                        {selectedRequest.uploaded_documents.length > 5 && (
                                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                                                +{selectedRequest.uploaded_documents.length - 5}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>


                {/* Reason & Notes */}
                <div className="space-y-4">
                    <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/10 dark:to-yellow-900/10 rounded-lg border border-amber-200 dark:border-amber-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Request Notes</h4>
                        </div>
                        <div className="bg-white/50 dark:bg-gray-800/50 rounded border border-amber-200 dark:border-amber-600 min-h-[100px] max-h-[120px] overflow-y-auto p-3">
                            <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                                {selectedRequest.request_notes || 'No notes provided for this request.'}
                            </p>
                        </div>
                    </div>

                    {/* Approval Notes Card */}
                    <div className="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800 dark:to-slate-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-gray-600 dark:bg-gray-400 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Approval Notes</h4>
                            <span className="text-xs text-gray-500 dark:text-gray-400">(Optional)</span>
                        </div>
                        <Textarea
                            value={approvalNotes}
                            onChange={(e) => setApprovalNotes(e.target.value)}
                            placeholder="Add your comments or notes for this approval decision..."
                            rows={3}
                            showCharCount={true}
                            maxLength={250}
                            className={cn(
                                "w-full rounded border transition-colors duration-200 text-xs",
                                "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-1 focus:ring-sidebar-primary/20 dark:focus:border-sidebar-primary dark:focus:ring-sidebar-primary/30",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            These notes will be visible to the employee and other approvers.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderAdvanceSalaryDialog = () => (
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {/* Header Status Bar */}
            <div className="flex items-center justify-between p-2 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${selectedRequest.status === 'pending' ? 'bg-yellow-500' :
                        selectedRequest.status === 'approved' ? 'bg-green-500' :
                            'bg-red-500'
                        }`}></div>
                    <div>
                        <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Advance Salary Request Review</h3>
                        <p className="text-xs text-gray-600 dark:text-gray-300">Please review all details carefully</p>
                    </div>
                </div>
                <StatusBadge status={selectedRequest.status} />
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Employee & Request Info */}
                <div className="space-y-4">
                    <div className="bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-900/10 dark:to-sky-900/10 rounded-lg border border-blue-200 dark:border-blue-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Employee Information</h4>
                        </div>
                        <div className="space-y-2 text-xs">
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Name:</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.employee_name || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Employee ID:</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.employee_id || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Department:</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.department?.name || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Designation:</span>
                                <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.request_from?.designation?.name || '-'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/10 dark:to-violet-900/10 rounded-lg border border-purple-200 dark:border-purple-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Advance Details</h4>
                        </div>
                        <div className="space-y-2 text-xs">
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Amount Requested:</span>
                                <span className="font-medium text-gray-800 dark:text-white">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(selectedRequest.advance_requested)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Request Date:</span>
                                <span className="font-medium text-gray-800 dark:text-white">{selectedRequest.date || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">EMI Period:</span>
                                <span className="font-medium text-gray-800 dark:text-white">{selectedRequest.emi_period} months</span>
                            </div>
                        </div>
                        <div className="mt-2">
                            <label className="block text-xs font-semibold text-gray-800 dark:text-white mb-2">Debit From <span className="text-red-500">*</span></label>
                            <DatePicker
                                value={debitFrom ? dayjs(debitFrom) : null}
                                onChange={(date, dateString) => setDebitFrom(dateString)}
                                placeholder="Select debit from month"
                                className={cn(
                                    "w-full rounded border transition-colors duration-200 text-xs px-3 py-2",
                                    "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                    "focus:border-sidebar-primary focus:ring-1 focus:ring-sidebar-primary/20 dark:focus:border-sidebar-primary dark:focus:ring-sidebar-primary/30",
                                    "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                                )}
                            />
                        </div>
                    </div>

                </div>

                {/* Reason & Notes */}
                <div className="space-y-4">
                    <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/10 dark:to-yellow-900/10 rounded-lg border border-amber-200 dark:border-amber-700 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Reason</h4>
                        </div>
                        <div className="bg-white/50 dark:bg-gray-800/50 rounded border border-amber-200 dark:border-amber-600 min-h-[100px] max-h-[120px] overflow-y-auto p-3">
                            <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                                {selectedRequest.reason || 'No reason provided for this request.'}
                            </p>
                        </div>
                    </div>

                    {/* Approval Notes Card */}
                    <div className="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800 dark:to-slate-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-gray-600 dark:bg-gray-400 rounded-full"></div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Approval Notes</h4>
                            <span className="text-xs text-gray-500 dark:text-gray-400">(Optional)</span>
                        </div>
                        <Textarea
                            value={approvalNotes}
                            onChange={(e) => setApprovalNotes(e.target.value)}
                            placeholder="Add your comments or notes for this approval decision..."
                            rows={3}
                            showCharCount={true}
                            maxLength={250}
                            className={cn(
                                "w-full rounded border transition-colors duration-200 text-xs",
                                "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-1 focus:ring-sidebar-primary/20 dark:focus:border-sidebar-primary dark:focus:ring-sidebar-primary/30",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            These notes will be visible to the employee and other approvers.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderResignationDialog = () => {
        const isFinalLevel = String(selectedRequest.level) === '2';

        const onNoticePeriodChange = (val) => {
            const numeric = val.replace(/[^0-9]/g, '');
            const computed = numeric === '' ? null : dayjs().add(parseInt(numeric, 10), 'day');
            setResignationApproval((prev) => ({
                ...prev,
                notice_period: numeric,
                last_working_day: computed,
            }));
        };

        const onLwdChange = (date) => {
            const days = date ? Math.max(0, date.diff(dayjs().startOf('day'), 'day')) : '';
            setResignationApproval((prev) => ({
                ...prev,
                last_working_day: date,
                notice_period: days === '' ? '' : String(days),
            }));
        };

        return (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                {/* Header Status Bar */}
                <div className="flex items-center justify-between p-2 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                    <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${selectedRequest.status === 'pending' ? 'bg-yellow-500' :
                            selectedRequest.status === 'approved' ? 'bg-green-500' :
                                'bg-red-500'
                            }`}></div>
                        <div>
                            <h3 className="text-sm font-semibold text-gray-800 dark:text-white">
                                Resignation Request Review {isFinalLevel && <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] bg-purple-100 text-purple-700 border border-purple-200">Final Approval</span>}
                            </h3>
                            <p className="text-xs text-gray-600 dark:text-gray-300">Please review and capture the notice period before approving</p>
                        </div>
                    </div>
                    <StatusBadge status={selectedRequest.status} />
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Employee & Request Info */}
                    <div className="space-y-4">
                        <div className="bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-900/10 dark:to-sky-900/10 rounded-lg border border-blue-200 dark:border-blue-700 p-4 shadow-sm">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Employee Information</h4>
                            </div>
                            <div className="space-y-2 text-xs">
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Name:</span>
                                    <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.employee?.employee_name || '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Employee ID:</span>
                                    <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.employee?.employee_id || '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Department:</span>
                                    <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.employee?.department?.name || '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Designation:</span>
                                    <span className="font-medium text-gray-800 dark:text-white text-right">{selectedRequest.employee?.designation?.name || '-'}</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/10 dark:to-violet-900/10 rounded-lg border border-purple-200 dark:border-purple-700 p-4 shadow-sm">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Resignation Details</h4>
                            </div>
                            <div className="space-y-2 text-xs">
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Request Date:</span>
                                    <span className="font-medium text-gray-800 dark:text-white">{selectedRequest.request_date?.split('T')[0] || '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Existing Notice Period:</span>
                                    <span className="font-medium text-gray-800 dark:text-white">{selectedRequest.notice_period || '-'}</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/10 dark:to-yellow-900/10 rounded-lg border border-amber-200 dark:border-amber-700 p-4 shadow-sm">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                                <h4 className="text-xs font-semibold text-gray-800 dark:text-white">Reason</h4>
                            </div>
                            <div className="bg-white/50 dark:bg-gray-800/50 rounded border border-amber-200 dark:border-amber-600 min-h-[100px] max-h-[120px] overflow-y-auto p-3">
                                <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                                    {selectedRequest.reason || 'No reason provided for this request.'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Approval form */}
                    <div className="space-y-4">
                        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/10 dark:to-teal-900/10 rounded-lg border border-emerald-200 dark:border-emerald-700 p-4 shadow-sm">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                                <h4 className="text-xs font-semibold text-gray-800 dark:text-white">
                                    {isFinalLevel ? 'Final Approval' : 'Approval'}
                                </h4>
                            </div>
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Notice Period (days) <span className="text-red-500">*</span>
                                        </label>
                                        <Input
                                            type="text"
                                            value={resignationApproval.notice_period}
                                            onChange={(e) => onNoticePeriodChange(e.target.value)}
                                            placeholder="e.g. 30"
                                            maxLength={3}
                                            className="w-full"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Last Working Day <span className="text-red-500">*</span>
                                        </label>
                                        <DatePicker
                                            value={resignationApproval.last_working_day ? dayjs(resignationApproval.last_working_day) : null}
                                            onChange={onLwdChange}
                                            format="DD-MM-YYYY"
                                            className="w-full"
                                            disabledDate={(d) => d && d < dayjs().startOf('day')}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Approval Comments <span className="text-red-500">*</span>
                                    </label>
                                    <Textarea
                                        value={resignationApproval.approval_comments}
                                        onChange={(e) =>
                                            setResignationApproval((prev) => ({ ...prev, approval_comments: e.target.value }))
                                        }
                                        placeholder="Comments visible to the employee and other approvers..."
                                        rows={4}
                                        showCharCount={true}
                                        maxLength={150}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={cn(
                            "p-3 rounded-lg border text-xs",
                            "bg-blue-50 border-blue-200 text-blue-700",
                            "dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300"
                        )}>
                            {isFinalLevel
                                ? 'You are the final approver. Approving will move the resignation into the exit process (assets handover and final verification).'
                                : 'Approving will forward this resignation to the final approver. Both approvers must approve before the exit process can start.'}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderDefaultDialog = () => (
        <div className="text-center py-8">
            <p className="text-gray-500">No details available for this request type.</p>
        </div>
    );

    const TabButton = ({ tab, label, icon: Icon, activeTab, setActiveTab }) => (
        <button
            onClick={() => setActiveTab(tab)}
            className={`flex items-center px-3 py-2 text-sm font-medium rounded-md border border-gray-200 transition-colors whitespace-nowrap ${activeTab === tab
                ? 'bg-sidebar text-white dark:bg-sidebar-primary'
                : 'text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
        >
            {/* Icon only on mobile, icon + text on desktop */}
            {Icon && <Icon className="w-5 h-5 sm:mr-2" />}
            <span className="hidden sm:inline">{label}</span>
        </button>
    );

    // Add these icon components
    const LeaveIcon = ({ className = "w-4 h-4" }) => (
        <svg className={className} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
        </svg>
    );

    const ReceiptIcon = ({ className = "w-4 h-4" }) => (
        <svg className={className} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm2.5 3a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm6.207.293a1 1 0 00-1.414 0l-6 6a1 1 0 101.414 1.414l6-6a1 1 0 000-1.414zM12.5 10a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" clipRule="evenodd" />
        </svg>
    );

    const CurrencyIcon = ({ className = "w-4 h-4" }) => (
        <svg className={className} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 5a1 1 0 100 2h1a2 2 0 011.732 1H7a1 1 0 100 2h2.732A2 2 0 018 11H7a1 1 0 00-.707 1.707l3 3a1 1 0 001.414-1.414l-1.483-1.484A4.008 4.008 0 0011.874 10H13a1 1 0 100-2h-1.126a3.976 3.976 0 00-.41-1H13a1 1 0 100-2H7z" clipRule="evenodd" />
        </svg>
    );

    return (
        <ContainerCard>

            <ModuleTitleCard
                Icon={LucideClipboardCheck}
                title={'Approval Inbox'}
                subtitle="Review and manage pending approval requests"
                totalRecords={totalRecords}
            />

            <Reactable
                pagename="approval inbox"
                tableTitle="Approval Inbox"
                columns={tableData.columns}
                data={tableData.data}
                setData={tableData.setData}
                url={tableData.url}
                setTotalCount={setTotalRecords}
                serverTrigger={serverTrigger}
                serverSide={true}
                tableHeight="calc(100vh - 370px)"
                rowSize="md"
                isCSVRequired
                isPDFRequired
                activeTab={activeTab}
                isDateRequired={false}
                isMonthYearRequired={false}
                isFromToDateRequired={false}
            >
                <div className="flex space-x-2 border-b border-gray-200 pb-2">
                    <div className="flex space-x-2 border-b border-gray-200 pb-2 overflow-x-auto">
                        <TabButton
                            tab="leave"
                            label="Leave Requests"
                            icon={LeaveIcon}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                        />
                        <TabButton
                            tab="reimbursement"
                            label="Reimbursements"
                            icon={ReceiptIcon}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                        />
                        <TabButton
                            tab="advance"
                            label="Advance Salary"
                            icon={CurrencyIcon}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                        />
                        <TabButton
                            tab="resign"
                            label="Resignation"
                            icon={UserX}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                        />
                    </div>
                </div>
            </Reactable>

            {/* Generic Approve Dialog for all request types */}
            {(() => {
                // Check if the request date has passed (for approve button)
                const isRequestDatePassed = () => {
                    if (!selectedRequest?.from_date) return false;

                    try {
                        const requestDate = new Date(selectedRequest.from_date);
                        const today = new Date();
                        requestDate.setHours(0, 0, 0, 0);
                        today.setHours(0, 0, 0, 0);
                        return requestDate < today;
                    } catch (error) {
                        return false;
                    }
                };

                // Only disable for leave requests when date has passed
                const isLeaveRequest = selectedRequest?.leave_request_type === 'leave' || selectedRequest?.approval_type === 'Leave';

                const shouldDisableApproveReject = selectedRequest?.status !== 'pending' && selectedRequest?.status !== 'cancelled' && selectedRequest?.status !== 'cancel inprogress';

                // Additional condition for leave requests: also disable if date has passed
                const shouldDisableLeaveApproveReject = isLeaveRequest && isRequestDatePassed();

                // Final disable condition: either status is not pending OR (for leave requests, date has passed)
                const finalDisableApproveReject = shouldDisableApproveReject || shouldDisableLeaveApproveReject;

                const canApprove = !shouldDisableApproveReject;

                return (
                    <DialogModal
                        open={showApproveDialog}
                        onClose={() => {
                            setShowApproveDialog(false);
                            setApprovalNotes('');
                            setDebitFrom('');
                            setResignationApproval({ notice_period: '', last_working_day: null, approval_comments: '' });
                        }}
                        {...(canApprove ? { onSave: handleApproveRequest, saveLabel: 'Approve' } : { hideSaveButton: true })}
                        title={`Review ${tableData.type?.charAt(0).toUpperCase() + tableData.type?.slice(1)} Request`}
                        showCancel={false}
                        width="60rem"
                        className="max-w-[95vw]"
                        loading={approveLoading}
                    >
                        {renderDialogContent()}
                    </DialogModal>
                );
            })()}

            {/* Processed Confirmation Dialog — only fires from reimbursement flow.
                Backend validates that the reimbursement is in approved/partial state
                before flipping to processed and stamping processed_by/date. */}
            <DialogModal
                open={showProcessedDialog}
                onClose={() => {
                    setShowProcessedDialog(false);
                    setProcessedRemarks('');
                }}
                onSave={handleMarkProcessed}
                title="Mark Reimbursement as Processed"
                saveLabel="Confirm"
                width="500px"
                loading={processedLoading}
            >
                <div className="space-y-4">
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-md">
                        <p className="text-sm text-emerald-800 dark:text-emerald-300">
                            Confirm that payment / accounting processing has been completed for this reimbursement.
                            This will finalize the request and notify the employee.
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Remarks (optional)
                        </label>
                        <Textarea
                            value={processedRemarks}
                            onChange={(e) => setProcessedRemarks(e.target.value)}
                            placeholder="e.g. UTR / payment reference, batch number..."
                            rows={3}
                            showCharCount={true}
                            maxLength={250}
                            className="w-full"
                        />
                    </div>
                </div>
            </DialogModal>

            {/* Reject Confirmation Dialog — collects a required reason that is stored on the
                approval log (via approval_notes) so the requester knows why it was rejected. */}
            <DialogModal
                open={showRejectDialog}
                onClose={() => {
                    setShowRejectDialog(false);
                    setSelectedRequestForReject(null);
                    setRevertReason('');
                }}
                onSave={handleConfirmReject}
                title={`Reject ${tableData.type?.charAt(0).toUpperCase() + tableData.type?.slice(1)} request`}
                saveLabel="Reject"
                saveButtonColor="destructive"
                width="500px"
                loading={rejectLoading}
            >
                <div className="space-y-4">
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-md">
                        <p className="text-sm text-red-800 dark:text-red-300">
                            Are you sure you want to reject this {tableData.type?.charAt(0).toUpperCase() + tableData.type?.slice(1)} request of{' '}
                            <strong className="font-semibold">
                                {selectedRequestForReject?.request_from?.employee_name
                                    ? selectedRequestForReject.request_from.employee_name.charAt(0).toUpperCase() + selectedRequestForReject.request_from.employee_name.slice(1)
                                    : 'Unknown Employee'}
                            </strong>{' '}
                            on{' '}
                            <strong className="font-semibold">
                                {selectedRequestForReject?.from_date || selectedRequestForReject?.date || selectedRequestForReject?.request_date || 'Unknown Date'}
                            </strong>
                            ? This action cannot be undone.
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Reason for Rejection <span className="text-red-500">*</span>
                        </label>
                        <Textarea
                            value={revertReason}
                            onChange={(e) => setRevertReason(e.target.value)}
                            placeholder="Please provide a reason for rejecting this request..."
                            rows={4}
                            showCharCount={true}
                            maxLength={250}
                            className="w-full"
                            required
                        />
                    </div>
                </div>
            </DialogModal>

            {/* Revert Approval Dialog (Generic for all types) */}
            <DialogModal
                open={showRevertDialog}
                onClose={() => {
                    setShowRevertDialog(false);
                    setRevertReason('');
                }}
                onSave={handleRevertApproval}
                title="Revert Approval"
                saveLabel="Revert"
                width="700px"
                loading={revertLoading}
            >
                {selectedRequest && (
                    <div className="space-y-4">
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-sm text-yellow-800">
                                You are about to revert the approval for this request. This action cannot be undone.
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Reason for Reverting Approval (Optional)
                            </label>
                            <Textarea
                                value={revertReason}
                                onChange={(e) => setRevertReason(e.target.value)}
                                placeholder="Please provide a reason for reverting this approval..."
                                rows={3}
                                showCharCount={true}
                                maxLength={250}
                                className={cn(
                                    "w-full rounded border transition-colors duration-200 text-xs",
                                    "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                    "focus:border-sidebar-primary focus:ring-1 focus:ring-sidebar-primary/20 dark:focus:border-sidebar-primary dark:focus:ring-sidebar-primary/30",
                                    "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                                )}
                                required
                            />
                        </div>
                        <div className="text-sm flex items-center justify-between gap-2 text-gray-600 border-t pt-4">
                            <div>
                                {(() => {
                                    const record = selectedRequest;
                                    let empDetails = {
                                        ...record?.request_from,
                                        designation_id: record?.request_from?.designation
                                            ? {
                                                id: record?.request_from.designation.id,
                                                designation_name: record?.request_from.designation.name,
                                            }
                                            : { id: 0, designation_name: "" },
                                        first_name: record?.request_from?.employee_name || "-",
                                        id: record?.request_from?.employee_id || "-",
                                    };
                                    return <EmployeeDetails details={empDetails} size="xs" />;
                                })()}
                            </div>
                            <p><strong>Request Type:</strong> {tableData.type?.charAt(0).toUpperCase() + tableData.type?.slice(1)}</p>
                            <p><strong>Current Status:</strong> <StatusBadge status={selectedRequest.status} /></p>
                        </div>
                    </div>
                )}
            </DialogModal>

            {/* Approval Log Dialog (Generic for all types) */}
            <DialogModal
                open={showApprovalLog}
                onClose={() => setShowApprovalLog(false)}
                title="Approval Log"
                showActions={false}
                width="750px"
            >
                {selectedRequest && selectedRequest.approval_log && selectedRequest.approval_log.length > 0 ? (
                    <div className="space-y-4">
                        <div className="overflow-x-auto h-60 relative">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date & Time
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Approver
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Notes
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {selectedRequest.approval_log.map((log, index) => (
                                        <tr key={index} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                {new Date(log.approved_date_and_time).toLocaleString()}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                {log.approver_name || '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <StatusBadge status={log.approval_status} />
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                {log.approval_notes || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="text-sm flex items-center justify-between gap-2 text-gray-600 border-t pt-4">
                            <div>
                                {(() => {
                                    const record = selectedRequest;
                                    let empDetails = {
                                        ...record?.request_from,
                                        designation_id: record?.request_from?.designation
                                            ? {
                                                id: record?.request_from.designation.id,
                                                designation_name: record?.request_from.designation.name,
                                            }
                                            : { id: 0, designation_name: "" },
                                        first_name: record?.request_from?.employee_name || "-",
                                        id: record?.request_from?.employee_id || "-",
                                    };
                                    return <EmployeeDetails details={empDetails} size="xs" />;
                                })()}
                            </div>
                            <p><strong>Request Type:</strong> {tableData.type?.charAt(0).toUpperCase() + tableData.type?.slice(1)}</p>
                            <p><strong>Current Status:</strong> <StatusBadge status={selectedRequest.status} /></p>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <p className="text-gray-500">No approval log available for this request.</p>
                    </div>
                )}
            </DialogModal>

        </ContainerCard>
    );
}

export default ApprovalInbox;